/**
 * PII masking for screenshot capture.
 *
 * Antes de capturar a screenshot, percorre os text nodes do DOM e mascara
 * temporariamente CPF, CNPJ, telefone e email aplicando uma estratégia de
 * substituição de texto. A função retorna um undo function que restaura o
 * texto original após a captura.
 *
 * Estratégia: substituímos o texto dos nodes diretamente (não usando classes
 * CSS) porque html-to-image renderiza o texto literal que está no DOM no
 * momento da captura. Mascaramento via CSS (color: transparent) deixaria
 * o conteúdo ainda visível na imagem renderizada.
 */

const PATTERNS: { name: string; regex: RegExp; mask: (match: string) => string }[] = [
  // CPF: 999.999.999-99
  {
    name: "cpf",
    regex: /\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g,
    mask: () => "***.***.***-**",
  },
  // CNPJ: XX.XXX.XXX/XXXX-99 — posições 1-12 alfanuméricas (RFB 2.229/2024); DV (13-14) são sempre dígitos.
  {
    name: "cnpj",
    regex: /\b[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}\b/g,
    mask: () => "**.***.***/****-**",
  },
  // Telefone BR: (99) 99999-9999 ou (99) 9999-9999
  {
    name: "phone",
    regex: /\(\d{2}\)\s?\d{4,5}-\d{4}/g,
    mask: () => "(**) *****-****",
  },
  // Email
  {
    name: "email",
    regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    mask: (m) => {
      const [local, domain] = m.split("@")
      if (!domain) return "***@***.***"
      return `${local[0] ?? "*"}***@${domain.split(".").slice(-1)[0]}.***`
    },
  },
]

interface MaskedNode {
  node: Text
  original: string
}

/**
 * Mascara PII visível no DOM. Retorna função para reverter.
 * SAFE: ignora nodes dentro de <input>, <textarea> e elementos com [data-no-mask].
 */
export function maskPiiInDom(): () => void {
  if (typeof document === "undefined") return () => {}

  const root = document.body
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      const tag = parent.tagName
      if (tag === "SCRIPT" || tag === "STYLE" || tag === "INPUT" || tag === "TEXTAREA") {
        return NodeFilter.FILTER_REJECT
      }
      if (parent.closest("[data-no-mask]")) return NodeFilter.FILTER_REJECT
      const text = node.nodeValue ?? ""
      if (!text.trim()) return NodeFilter.FILTER_REJECT
      // Quick check: skip if no digit and no @
      if (!/\d/.test(text) && !text.includes("@")) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })

  const masked: MaskedNode[] = []
  const textNodes: Text[] = []
  let current: Node | null
  while ((current = walker.nextNode())) {
    textNodes.push(current as Text)
  }

  for (const node of textNodes) {
    const original = node.nodeValue ?? ""
    let updated = original
    for (const p of PATTERNS) {
      updated = updated.replace(p.regex, (m) => p.mask(m))
    }
    if (updated !== original) {
      node.nodeValue = updated
      masked.push({ node, original })
    }
  }

  return function restore() {
    for (const { node, original } of masked) {
      try {
        node.nodeValue = original
      } catch {
        /* node may have been detached */
      }
    }
  }
}
